import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Progress,
} from "@ancatag/at-design";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { apiFetch } from "../../services/apiFetch";
import type { ModelConfig } from "../../services/models";
import type { GenerationType } from "../../services/provider-templates";
import {
	type CreateRouteConfigData,
	createRouteConfig,
} from "../../services/route-configs";
import { Step1GenerationType } from "./steps/Step1GenerationType";
import { Step3SelectModels } from "./steps/Step3SelectModels";
import { Step5Configuration } from "./steps/Step5Configuration";

export interface ModelConfigWithPriority {
	modelConfig: ModelConfig;
	isPrimary: boolean;
	priority: number;
}

export interface WizardData {
	generationType: GenerationType | null;
	selectedModelConfigs: ModelConfigWithPriority[]; // Model configs with primary flag and priority
	configName: string;
	description: string;
	parameters: {
		temperature?: number;
		maxTokens?: number;
		topP?: number;
		topK?: number;
		repeatPenalty?: number;
		contextWindow?: number;
		stopSequences?: string[];
	};
	systemPrompt: string;
	prePromptItems: string[];
	hotCacheEnabled: boolean;
	semanticCacheEnabled: boolean;
	customCacheTtl?: number;
	ragEnabled: boolean;
	embeddingModelConfigId?: string;
	responseFormat?: any;
	draftConfigId?: string; // Track draft config created after Step 3
}

const TOTAL_STEPS = 3;

export function NewModelConfig() {
	const [, setLocation] = useLocation();
	const [currentStep, setCurrentStep] = useState(1);
	const [saving, setSaving] = useState(false);
	const [wizardData, setWizardData] = useState<WizardData>({
		generationType: null,
		selectedModelConfigs: [],
		configName: "",
		description: "",
		parameters: {},
		systemPrompt: "",
		prePromptItems: [],
		hotCacheEnabled: true,
		semanticCacheEnabled: true,
		ragEnabled: false,
		responseFormat: undefined,
	});

	const updateWizardData = (updates: Partial<WizardData>) => {
		setWizardData((prev) => ({ ...prev, ...updates }));
	};

	const canProceed = () => {
		switch (currentStep) {
			case 1:
				return wizardData.generationType !== null;
			case 2:
				return wizardData.selectedModelConfigs.length > 0;
			case 3:
				return wizardData.configName.trim().length > 0;
			case 4:
				return true; // Step 4 is optional, can always proceed
			default:
				return false;
		}
	};

	const handleNext = () => {
		if (canProceed() && currentStep < TOTAL_STEPS) {
			setCurrentStep((prev) => prev + 1);
		}
	};

	const handleBack = () => {
		if (currentStep > 1) {
			setCurrentStep((prev) => prev - 1);
		}
	};

	const handleFinish = async () => {
		if (!canProceed()) {
			toast.error("Please complete all required fields");
			return;
		}

		if (wizardData.selectedModelConfigs.length === 0) {
			toast.error("Please add at least one model config");
			return;
		}

		setSaving(true);
		try {
			// Create RouteConfig (without provider/modelIdentifier - those are in ModelConfigs)
			const configData: CreateRouteConfigData = {
				name: wizardData.configName,
				description: wizardData.description || undefined,
				generationType: wizardData.generationType!,
				parameters:
					Object.keys(wizardData.parameters).length > 0
						? wizardData.parameters
						: undefined,
				systemPrompt: wizardData.systemPrompt || undefined,
				prePromptItems:
					wizardData.prePromptItems.length > 0
						? wizardData.prePromptItems
						: undefined,
				hotCacheEnabled: wizardData.hotCacheEnabled,
				semanticCacheEnabled: wizardData.semanticCacheEnabled,
				customCacheTtl: wizardData.customCacheTtl,
				ragEnabled: wizardData.ragEnabled,
				embeddingModelConfigId: wizardData.embeddingModelConfigId,
				responseFormat: wizardData.responseFormat,
			};

			const config = await createRouteConfig(configData);
			const configId = config.id;

			// Link ModelConfigs to RouteConfig
			for (const modelConfigWithPriority of wizardData.selectedModelConfigs) {
				await apiFetch(`/route-configs/${configId}/model-configs`, {
					method: "POST",
					body: JSON.stringify({
						modelConfigId: modelConfigWithPriority.modelConfig.id,
						isPrimary: modelConfigWithPriority.isPrimary,
						priority: modelConfigWithPriority.priority,
					}),
				});
			}

			toast.success("Route configuration created successfully!");
			setLocation("/route-configs");
		} catch (error: any) {
			const errorMessage =
				error.message || "Failed to create route configuration";
			const errorCode = error.code || "";

			// Check for specific error types
			if (errorCode === "FORBIDDEN" || errorMessage.includes("limit")) {
				toast.error("Route Config Limit Reached", {
					description:
						"You've reached the maximum number of route configurations for your plan. Please delete an existing config or upgrade your plan.",
					duration: 8000,
				});
			} else {
				toast.error(errorMessage);
			}
		} finally {
			setSaving(false);
		}
	};

	const stepTitles = [
		"Select Generation Type",
		"Select Models",
		"Configuration",
		"Routing & Traffic Rules",
	];

	return (
		<div className="container mx-auto max-w-4xl py-4 md:py-8 px-4">
			<div className="mb-6">
				<Button
					variant="ghost"
					onClick={() => setLocation("/playground")}
					className="mb-4 h-10"
				>
					<ArrowLeft className="h-4 w-4 mr-2" />
					Back to Playground
				</Button>
				<h1 className="text-2xl md:text-3xl font-bold">
					Create Route Configuration
				</h1>
				<p className="text-muted-foreground mt-2 text-sm md:text-base">
					Step {currentStep} of {TOTAL_STEPS}: {stepTitles[currentStep - 1]}
				</p>
			</div>

			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle>{stepTitles[currentStep - 1]}</CardTitle>
							<CardDescription>
								{currentStep === 1 &&
									"Choose the type of generation this model will handle"}
								{currentStep === 2 && "Select model configs"}
								{currentStep === 3 &&
									"Configure generation parameters, caching, and RAG"}
							</CardDescription>
						</div>
						<Button
							variant="ghost"
							size="icon"
							className="h-10 w-10"
							onClick={() => setLocation("/route-configs")}
							aria-label="Close"
						>
							<X className="h-5 w-5" />
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					<div className="mb-6">
						<Progress
							value={(currentStep / TOTAL_STEPS) * 100}
							className="h-2"
						/>
					</div>

					<div className="min-h-[300px] md:min-h-[400px]">
						{currentStep === 1 && (
							<Step1GenerationType
								selectedType={wizardData.generationType}
								onSelect={(type) => updateWizardData({ generationType: type })}
							/>
						)}
						{currentStep === 2 && (
							<Step3SelectModels
								generationType={wizardData.generationType!}
								selectedModelConfigs={wizardData.selectedModelConfigs}
								onModelConfigsChange={(modelConfigs) =>
									updateWizardData({ selectedModelConfigs: modelConfigs })
								}
							/>
						)}
						{currentStep === 3 && (
							<Step5Configuration
								wizardData={wizardData}
								onUpdate={updateWizardData}
							/>
						)}
					</div>

					<div className="sticky bottom-0 bg-card border-t pt-4 mt-8 -mx-6 px-6 pb-safe">
						<div className="flex flex-col sm:flex-row justify-between gap-3">
							<Button
								variant="outline"
								onClick={handleBack}
								disabled={currentStep === 1}
								className="w-full sm:w-auto h-10"
							>
								<ArrowLeft className="h-4 w-4 mr-2" />
								Back
							</Button>
							{currentStep < TOTAL_STEPS ? (
								<Button
									onClick={handleNext}
									disabled={!canProceed()}
									className="w-full sm:w-auto h-10"
								>
									Next
									<ArrowRight className="h-4 w-4 ml-2" />
								</Button>
							) : (
								<Button
									onClick={handleFinish}
									disabled={!canProceed() || saving}
									className="w-full sm:w-auto h-10"
								>
									{saving ? "Creating..." : "Create Route Configuration"}
								</Button>
							)}
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
